// Astuces de départ (socle UX, section 8) : cinq étapes qu'on fait glisser (le geste du jeu), montrées à chaque
// ouverture tant qu'on n'a pas de compte. Verrouillage : tant que la dernière étape n'a pas été vue, ni la croix, ni le
// fond, ni Échap ne ferment (sauf si rouvertes depuis les réglages : `dismissible`). « Continuer » avance d'une étape.
import { $ } from './dom.js';
import { createDialog } from './dialog.js';

/** Étape affichée d'après la position de défilement du rail. */
export const stepFromScroll = (scrollLeft, width, steps) => {
  if (!(width > 0)) return 0;
  return Math.max(0, Math.min(steps - 1, Math.round(scrollLeft / width)));
};

/** Libellé du bouton principal : « Continuer » sur les étapes intermédiaires, « C'est parti ! » sur la dernière. */
export const nextButtonKey = (step, steps) => (step >= steps - 1 ? 'help.letsGo' : 'help.continue');

/** @param {{ t: (key: string, params?: object) => string }} deps */
export function createHelpDialog({ t }) {
  const overlay = $('helpOverlay');
  const rail = $('helpRail');
  const dotsBox = $('helpDots');
  const next = $('helpNext');
  const slides = [...rail.querySelectorAll('.help-slide')];
  const total = slides.length;
  let step = 0;
  let lastSeen = false;
  let locked = true;
  let onCloseCallback = null;

  // `onClose` est appelé de façon synchrone par dialog.close(), dans le geste de fermeture (bouton, croix, fond, Échap),
  // avant tout await : c'est ce qui permet de lancer un son sur mobile à ce moment-là
  const dialog = createDialog(overlay, {
    onOpen(options) {
      lastSeen = false;
      locked = options.dismissible !== true;
      onCloseCallback = options.onClose ?? null;
      goTo(0, false);
      update();
    },
    onClose() {
      const callback = onCloseCallback;
      onCloseCallback = null;
      callback?.();
    },
  });

  // Croix, fond et Échap n'agissent qu'une fois la dernière étape vue (ou si l'aide est quittable)
  const syncDismissible = () => dialog.setDismissible(!locked || lastSeen);

  dotsBox.replaceChildren(...slides.map((_, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'help-dot';
    dot.addEventListener('click', () => goTo(index));
    return dot;
  }));

  function update() {
    next.textContent = t(nextButtonKey(step, total));
    [...dotsBox.children].forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === step));
      dot.setAttribute('aria-label', t('help.step', { n: index + 1, total }));
    });
    slides.forEach((slide, index) => slide.setAttribute('aria-label', t('help.step', { n: index + 1, total })));
    if (step === total - 1) lastSeen = true;
    syncDismissible();
  }

  function goTo(index, animate = true) {
    step = Math.max(0, Math.min(total - 1, index));
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    rail.scrollTo?.({ left: step * rail.clientWidth, behavior: animate && !reduce ? 'smooth' : 'auto' });
    update();
  }

  rail.addEventListener('scroll', () => {
    const seen = stepFromScroll(rail.scrollLeft, rail.clientWidth, total);
    if (seen !== step) { step = seen; update(); }
  });

  next.addEventListener('click', () => {
    if (step >= total - 1) dialog.close(null);
    else goTo(step + 1);
  });

  return {
    /** @param {{ dismissible?: boolean, onClose?: () => void }} [options]  dismissible: true = aucune obligation d'aller au bout */
    open: (options = {}) => dialog.open(options),
    close: dialog.close,
    isOpen: dialog.isOpen,
    refresh: update,
    get step() { return step; },
  };
}
